import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

// White checkmark for the checked checkbox state (Figma node 3612:78213, 17px).
const CheckIcon = () => (
  <svg width="14" height="14" viewBox="0 0 17 17" fill="none" aria-hidden="true">
    <path
      d="M3.5 8.5l3.5 3.5 6.5-7"
      stroke="white"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

interface CheckboxRowProps {
  checked: boolean;
  label: string;
  onToggle: () => void;
}

// A single checkbox row matching Figma "Check box" (node 3612:77327): a 26px box
// (1.5px grey border when off, green fill + white tick when on) and a label that
// darkens when checked. Used by the Rooms, Price and Amenities sections.
export const CheckboxRow = ({ checked, label, onToggle }: CheckboxRowProps) => (
  <button
    type="button"
    className={`cbf-check${checked ? ' cbf-check--on' : ''}`}
    role="checkbox"
    aria-checked={checked}
    onClick={onToggle}
  >
    <span className="cbf-check__box" aria-hidden="true">{checked && <CheckIcon />}</span>
    <span className="cbf-check__label">{label}</span>
  </button>
);

// Relative bar heights for the static price-distribution chart (no live
// distribution endpoint yet — JAA-277 follow-up). Shape mirrors Figma 3612:77497.
const BAR_HEIGHTS = [
  0, 0, 0, 0, 0, 0, 2, 11, 26, 32, 44, 51, 51, 57, 72, 88, 81, 83, 72, 51, 39, 29, 29, 22, 20, 25,
  16, 13, 12, 13,
];
const BAR_MAX = 88;
// Price domain the slider spans (€). Handles snap to STEP.
const PRICE_MAX_BOUND = 1000000;
const PRICE_STEP = 10000;


interface PriceHistogramProps {
  min: string;
  max: string;
  onChange: (updates: { priceMin?: string; priceMax?: string }) => void;
}

// Price-range histogram with a dual-thumb slider (Figma node 3612:78407). The
// bars are a static distribution placeholder; the two handles drive priceMin /
// priceMax over a fixed € domain and stay synced with the Min/Max inputs below.
export const PriceHistogram = ({ min, max, onChange }: PriceHistogramProps) => {
  const { t } = useTranslation();
  const trackRef = useRef<HTMLDivElement>(null);
  const minVal = min === '' ? 0 : Number(min);
  const maxVal = max === '' ? PRICE_MAX_BOUND : Number(max);
  const minPct = Math.min(100, (minVal / PRICE_MAX_BOUND) * 100);
  const maxPct = Math.min(100, (maxVal / PRICE_MAX_BOUND) * 100);

  // The teardown of the drag in progress, so an unmount mid-drag can run it.
  const endDragRef = useRef<(() => void) | null>(null);
  useEffect(() => () => endDragRef.current?.(), []);

  // Drag a handle: translate pointer X over the track into a stepped € value,
  // clamped so the two thumbs never cross.
  const startDrag = (handle: 'min' | 'max') => (e: React.PointerEvent) => {
    e.preventDefault();
    const move = (ev: PointerEvent) => {
      if (ev.pointerId !== e.pointerId) return;
      const rect = trackRef.current?.getBoundingClientRect();
      if (!rect || rect.width === 0) return;
      const ratio = Math.min(1, Math.max(0, (ev.clientX - rect.left) / rect.width));
      const value = Math.round((ratio * PRICE_MAX_BOUND) / PRICE_STEP) * PRICE_STEP;
      if (handle === 'min') onChange({ priceMin: String(Math.min(value, maxVal)) });
      else onChange({ priceMax: String(Math.max(value, minVal)) });
    };
    // Ends this drag. The listeners are on window, so they also see other
    // pointers: a second finger panning a nearby scroller fires pointercancel
    // and would otherwise kill a drag that is still going. Only the pointer
    // that started it ends it — the unmount cleanup calls this with no event.
    const up = (ev?: PointerEvent) => {
      if (ev && ev.pointerId !== e.pointerId) return;
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      window.removeEventListener('pointercancel', up);
      endDragRef.current = null;
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    window.addEventListener('pointercancel', up);
    endDragRef.current = up;
  };

  return (
    <div className="cbf-histogram">
      <div className="cbf-histogram__bars">
        {BAR_HEIGHTS.map((h, i) => {
          // A bar is in-range when its price position sits between the two thumbs;
          // out-of-range bars render grey (Figma).
          const barPct = ((i + 0.5) / BAR_HEIGHTS.length) * 100;
          const inRange = barPct >= minPct && barPct <= maxPct;
          return (
            <span
              key={i}
              className={`cbf-histogram__bar${inRange ? '' : ' cbf-histogram__bar--out'}`}
              style={{ height: `${Math.max(2, (h / BAR_MAX) * 100)}%` }}
            />
          );
        })}
      </div>
      <div className="cbf-histogram__track" ref={trackRef}>
        <div className="cbf-histogram__baseline" />
        <div
          className="cbf-histogram__range"
          style={{ left: `${minPct}%`, width: `${Math.max(0, maxPct - minPct)}%` }}
        />
        <button
          type="button"
          className="cbf-histogram__handle"
          style={{ left: `${minPct}%` }}
          onPointerDown={startDrag('min')}
          aria-label={t('newLandingPage.couldBuy.criteria.priceMinHandleAria')}
        />
        <button
          type="button"
          className="cbf-histogram__handle"
          style={{ left: `${maxPct}%` }}
          onPointerDown={startDrag('max')}
          aria-label={t('newLandingPage.couldBuy.criteria.priceMaxHandleAria')}
        />
      </div>
    </div>
  );
};
